// VisitDetail — site-visit call report (WP-H revamp). Four tabs (Overview /
// Discussion / Actions / Files) via TabRail + ?visitTab= URL param. Review deck
// expands inline on Overview. Owns GET /visits/detail (+ 15s poll while meetings
// process) and page-wide drag-drop → ImportTranscriptModal.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Card, EntityHeader, Button, TextField, TextArea, CenteredSpinner, EmptyState, Alert,
  RecordCard, TabRail, Modal, Badge, Pill, Icon,
  type EntityStatus,
} from "../../design/ui";
import {
  getVisitDetail, patchVisit, acceptCarryOverItem, getCustomerTickets,
  exportVisit, auditVisit, deleteVisit, listFiles, presignFile,
  type VisitDetailResponse, type VisitStatus, type CustomerTickets, type StoreObject,
  type Meeting,
} from "../../lib/brain-api";
import { useProfile } from "../../lib/profile";
import { CustomerMeetingsTab, MEETING_PROCESSING_STATUSES } from "../meetings/CustomerMeetingsTab";
import { TicketsTable, NoTicketMappingNote } from "../tickets/CustomerTicketsTab";
import { ItemsTable } from "./ItemsTable";
import { ImportTranscriptModal } from "./ImportTranscriptModal";
import { ReviewDeck } from "./ReviewDeck";
import { FilesTab } from "./FilesTab";
import { ActionsTab } from "./ActionsTab";

type VisitTab = "overview" | "discussion" | "actions" | "files";
type VisitUiState = "empty" | "analyzing" | "review" | "done";
type PipelineStep = "uploaded" | "transcribing" | "extracting" | "ready";

function visitStatusBadge(status: VisitStatus): EntityStatus {
  if (status === "audited") return "active";
  if (status === "ready") return "paused";
  return "onboarding";
}

function stageLabel(status: VisitStatus): string {
  if (status === "audited") return "Audited";
  if (status === "ready") return "Ready for audit";
  return "In progress";
}

function deriveUiState(meetings: Meeting[], proposedCount: number, audited: boolean): VisitUiState {
  if (meetings.length === 0) return "empty";
  if (meetings.some((m) => MEETING_PROCESSING_STATUSES.has(m.status))) return "analyzing";
  if (proposedCount > 0) return "review";
  if (audited) return "done";
  return "done";
}

function pipelineStep(meetings: Meeting[], proposedCount: number): { current: PipelineStep | null; completedThrough: number } {
  // Steps: 0 uploaded, 1 transcribing, 2 extracting, 3 ready
  if (meetings.length === 0) return { current: null, completedThrough: -1 };
  const statuses = meetings.map((m) => m.status);
  const anyTranscribing = statuses.some((s) => s === "recording" || s === "uploading" || s === "transcribing");
  const anyExtracting = statuses.some((s) => s === "synthesizing");
  if (anyTranscribing) return { current: "transcribing", completedThrough: 0 };
  if (anyExtracting) return { current: "extracting", completedThrough: 1 };
  if (proposedCount > 0) return { current: "ready", completedThrough: 2 };
  // All meetings settled and nothing left to review
  return { current: null, completedThrough: 3 };
}

const PIPELINE_STEPS: { key: PipelineStep; label: string }[] = [
  { key: "uploaded", label: "Uploaded" },
  { key: "transcribing", label: "Transcribing" },
  { key: "extracting", label: "Extracting" },
  { key: "ready", label: "Ready to review" },
];

const VD_CSS = `
.vd-overview { display: grid; grid-template-columns: 1fr 340px; gap: 18px; align-items: start; }
@media (max-width: 960px) { .vd-overview { grid-template-columns: 1fr; } }
.vd-col { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.vd-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
@media (max-width: 720px) { .vd-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.vd-stat {
  background: var(--kt-surface); border: 1px solid var(--kt-border); border-radius: 12px;
  padding: 12px 14px; text-align: left;
}
.vd-stat--click { cursor: pointer; }
.vd-stat--amber { background: #fff9ef; border-color: #f0d9a8; }
.vd-stat-label { font-size: 11px; color: var(--kt-text-muted); margin-bottom: 4px; }
.vd-stat-value { font-family: var(--font-mono); font-size: 18px; font-weight: 600; color: var(--kt-ink-strong); }
.vd-stat--amber .vd-stat-value { color: #b45309; }
.vd-banner {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  background: linear-gradient(90deg, #0d9488, #0f766e); color: #fff;
  border-radius: 14px; padding: 14px 18px;
}
.vd-banner p { margin: 0; font-size: 13.5px; opacity: 0.92; }
.vd-drop {
  border: 2px dashed var(--kt-border-strong); border-radius: 16px; padding: 48px 24px;
  text-align: center; background: var(--kt-surface);
}
.vd-pipe { display: flex; align-items: center; gap: 0; padding: 12px 4px 4px; flex-wrap: wrap; }
.vd-pipe-step { display: flex; align-items: center; gap: 8px; }
.vd-pipe-dot {
  width: 30px; height: 30px; border-radius: 999px; display: flex; align-items: center; justify-content: center;
  background: #eef1f0; color: var(--kt-text-muted); font-size: 14px; flex-shrink: 0;
}
.vd-pipe-dot.done, .vd-pipe-dot.current { background: #0d9488; color: #fff; }
.vd-pipe-dot.current { animation: vdPulse 1.4s ease-in-out infinite; }
@keyframes vdPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(13,148,136,0.35); } 50% { box-shadow: 0 0 0 6px rgba(13,148,136,0); } }
.vd-pipe-label { font-size: 12px; color: var(--kt-text-secondary); margin-right: 4px; white-space: nowrap; }
.vd-pipe-dot.done + .vd-pipe-label, .vd-pipe-dot.current + .vd-pipe-label { color: var(--kt-ink); font-weight: 500; }
.vd-pipe-line { width: 28px; height: 2px; background: #e5eae8; margin: 0 6px; flex-shrink: 0; }
.vd-pipe-line.done { background: #0d9488; }
`;

export function VisitDetail({ customerId, visitId, onBack }: {
  customerId: string;
  visitId: string;
  onBack: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useProfile();
  const isAdmin = profile?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get("visitTab") ?? "overview";
  const activeTab: VisitTab =
    rawTab === "discussion" || rawTab === "actions" || rawTab === "files" ? rawTab : "overview";
  const setTab = (tab: VisitTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("visitTab", tab);
      return next;
    }, { replace: true });
  };

  const [detail, setDetail] = useState<VisitDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reportsVersion, setReportsVersion] = useState(0);
  const [latestPdfUrl, setLatestPdfUrl] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await getVisitDetail(customerId, visitId));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load site visit");
    }
  }, [customerId, visitId]);

  useEffect(() => { void load(); }, [load]);

  const anyProcessing = (detail?.meetings ?? []).some((m) => MEETING_PROCESSING_STATUSES.has(m.status));
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!anyProcessing) return;
    const id = setInterval(() => void loadRef.current(), 15_000);
    return () => clearInterval(id);
  }, [anyProcessing]);

  const visit = detail?.visit;
  const meetings = detail?.meetings ?? [];
  const items = detail?.items ?? [];
  const actions = detail?.actions ?? [];
  const carryOver = detail?.carryOver ?? [];
  const audited = visit?.status === "audited";

  const proposedItems = items.filter((i) => i.reviewState === "proposed");
  const proposedActions = actions.filter((a) => a.reviewState === "proposed");
  const proposedCount = proposedItems.length + proposedActions.length;
  const acceptedItems = items.filter((i) => i.reviewState === "accepted");
  const acceptedActions = actions.filter((a) => a.reviewState === "accepted");

  const uiState = useMemo(
    () => (visit ? deriveUiState(meetings, proposedCount, audited) : "empty"),
    [visit, meetings, proposedCount, audited],
  );
  const pipe = pipelineStep(meetings, proposedCount);

  const openItems = acceptedItems.filter((i) => i.status !== "done" && i.status !== "dropped").length;
  const ticketsRaised = acceptedItems.filter((i) => !!i.jiraKey).length
    + acceptedActions.filter((a) => !!a.jiraKey).length;
  const actionsWithJira = acceptedActions.filter((a) => !!a.jiraKey).length;

  const dateRange = visit
    ? (visit.endsOn && visit.endsOn !== visit.startsOn ? `${visit.startsOn} → ${visit.endsOn}` : visit.startsOn)
    : "";

  const commitTitle = async (title: string) => {
    setEditingTitle(false);
    const t = title.trim();
    if (!visit || !t || t === visit.title) return;
    try {
      await patchVisit({ customerId, visitId, title: t });
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Couldn't rename — try again", { variant: "error" });
    }
  };

  const saveSummary = async () => {
    try {
      await patchVisit({ customerId, visitId, summary: summaryDraft.trim() || undefined });
      setEditingSummary(false);
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Couldn't save summary", { variant: "error" });
    }
  };

  const saveNotes = async () => {
    try {
      await patchVisit({ customerId, visitId, notesNextVisit: notesDraft.trim() || undefined });
      setEditingNotes(false);
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Couldn't save notes", { variant: "error" });
    }
  };

  const acceptCarryOver = async (item: (typeof carryOver)[0]) => {
    try {
      await acceptCarryOverItem({ customerId, visitId, fromItemId: item.id });
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Couldn't carry over — try again", { variant: "error" });
    }
  };

  const doExport = async () => {
    setExporting(true);
    setActionError(null);
    try {
      const result = await exportVisit({ customerId, visitId });
      setReportsVersion((v) => v + 1);
      if (result.pdfUrl) setLatestPdfUrl(result.pdfUrl);
      window.open(result.url, "_blank", "noopener");
      enqueueSnackbar(result.pdfUrl ? "Report exported (HTML + PDF)" : "Report exported (HTML)", { variant: "success" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Export failed";
      setActionError(msg);
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  const doAudit = async () => {
    setAuditing(true);
    try {
      await auditVisit({ customerId, visitId });
      setAuditOpen(false);
      await load();
      enqueueSnackbar("Visit marked audited", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Audit failed", { variant: "error" });
    } finally {
      setAuditing(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteVisit({ customerId, visitId });
      onBack();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Delete failed", { variant: "error" });
      setDeleting(false);
    }
  };

  const openReview = () => {
    setTab("overview");
    setReviewOpen(true);
  };

  const primaryCta = (() => {
    if (uiState === "empty") {
      return (
        <Button variant="primary" icon="plus" onClick={() => { setDroppedFile(null); setImportOpen(true); }}>
          Add transcript
        </Button>
      );
    }
    if (uiState === "analyzing") {
      return (
        <Button disabled>
          <span className="kt-spinner" style={{ width: 13, height: 13, borderWidth: 2, marginRight: 6 }} />
          Analyzing…
        </Button>
      );
    }
    if (uiState === "review") {
      return (
        <Button variant="primary" icon="list-check" onClick={openReview}>
          Review {proposedCount} suggestion{proposedCount === 1 ? "" : "s"}
        </Button>
      );
    }
    return (
      <Button variant="primary" icon={exporting ? undefined : "download"} disabled={exporting} onClick={() => void doExport()}>
        {exporting && <span className="kt-spinner" style={{ width: 13, height: 13, borderWidth: 2, marginRight: 6 }} />}
        {exporting ? "Exporting…" : "Export report"}
      </Button>
    );
  })();

  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!detail || !visit) return <CenteredSpinner label="Loading visit…" />;

  const tabSpecs = [
    { key: "overview", label: "Overview" },
    { key: "discussion", label: `Discussion${acceptedItems.length ? ` · ${acceptedItems.length}` : ""}` },
    { key: "actions", label: `Actions${acceptedActions.length ? ` · ${acceptedActions.length}` : ""}` },
    { key: "files", label: "Files" },
  ];

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) { setDroppedFile(f); setImportOpen(true); }
      }}
    >
      <style>{VD_CSS}</style>

      <EntityHeader
        name={
          editingTitle ? (
            <TextField defaultValue={visit.title} autoFocus onBlur={(e) => void commitTitle(e.target.value)} />
          ) : (
            <span onClick={() => setEditingTitle(true)} title="Click to rename" style={{ cursor: "text" }}>{visit.title}</span>
          )
        }
        status={visitStatusBadge(visit.status)}
        monoKey={`visit:${visit.id} · ${dateRange} · ${visit.type}`}
        actions={
          <>
            <Button icon="arrow-left" onClick={onBack}>Back</Button>
            {audited && <Badge tone="success" icon="check">Audited</Badge>}
            {primaryCta}
            {uiState === "done" && latestPdfUrl && (
              <Button icon="file-text" onClick={() => window.open(latestPdfUrl, "_blank", "noopener")}>View PDF</Button>
            )}
            {uiState !== "empty" && uiState !== "analyzing" && uiState !== "review" && (
              <Button icon="plus" onClick={() => { setDroppedFile(null); setImportOpen(true); }}>Add transcript</Button>
            )}
            {!audited && (
              <Button icon="lock-check" onClick={() => setAuditOpen(true)}>Mark audited</Button>
            )}
            {isAdmin && (
              <Button variant="danger" icon="trash" onClick={() => setDeleteOpen(true)}>Delete</Button>
            )}
          </>
        }
      />

      {meetings.length > 0 && (
        <Card>
          <div className="vd-pipe" role="list" aria-label="Pipeline status">
            {PIPELINE_STEPS.map((step, i) => {
              const done = pipe.completedThrough >= i;
              const current = pipe.current === step.key;
              return (
                <div key={step.key} className="vd-pipe-step" role="listitem">
                  {i > 0 && <div className={`vd-pipe-line${pipe.completedThrough >= i - 1 ? " done" : ""}`} />}
                  <div className={`vd-pipe-dot${done && !current ? " done" : ""}${current ? " current" : ""}`}>
                    {done && !current ? "✓" : current ? "…" : i + 1}
                  </div>
                  <span className="vd-pipe-label">{step.label}{step.key === "ready" && proposedCount > 0 ? ` (${proposedCount})` : ""}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {actionError && <Alert tone="danger">{actionError}</Alert>}

      <TabRail tabs={tabSpecs} activeTab={activeTab} onChange={(k) => setTab(k as VisitTab)} />

      {activeTab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {uiState === "empty" && !reviewOpen && (
            <div className="vd-drop">
              <Icon name="upload" size={28} />
              <h3 style={{ margin: "12px 0 6px", fontSize: 16 }}>No transcript yet</h3>
              <p style={{ fontSize: 13, color: "var(--kt-text-muted)", margin: "0 0 14px" }}>
                Drop a .txt / .md / .vtt file here, or add a Meet / Google Doc link.
              </p>
              <Button variant="primary" icon="plus" onClick={() => { setDroppedFile(null); setImportOpen(true); }}>
                Add transcript
              </Button>
            </div>
          )}

          {uiState === "review" && !reviewOpen && (
            <div className="vd-banner">
              <Icon name="list-check" size={22} color="#fff" />
              <div style={{ flex: 1, minWidth: 200 }}>
                <strong style={{ fontSize: 14.5 }}>{proposedCount} AI suggestion{proposedCount === 1 ? "" : "s"} ready to review</strong>
                <p>Accept, edit, or dismiss — nothing enters the report until you do.</p>
              </div>
              <Button
                style={{ background: "#fff", color: "#0f766e", border: "none" }}
                onClick={openReview}
              >
                Review suggestions
              </Button>
            </div>
          )}

          {reviewOpen && (
            <ReviewDeck
              customerId={customerId}
              visitId={visitId}
              items={items}
              actions={actions}
              carryOver={carryOver}
              onChanged={() => void load()}
              onClose={() => setReviewOpen(false)}
              onExport={() => { setReviewOpen(false); void doExport(); }}
            />
          )}

          {!reviewOpen && uiState !== "empty" && (
            <div className="vd-overview">
              <div className="vd-col">
                <div className="vd-stats">
                  <div className="vd-stat">
                    <div className="vd-stat-label">Discussion</div>
                    <div className="vd-stat-value">{openItems} / {acceptedItems.length}</div>
                  </div>
                  <div className="vd-stat">
                    <div className="vd-stat-label">Actions (Jira)</div>
                    <div className="vd-stat-value">{actionsWithJira} / {acceptedActions.length}</div>
                  </div>
                  <div className="vd-stat">
                    <div className="vd-stat-label">Tickets raised</div>
                    <div className="vd-stat-value">{ticketsRaised}</div>
                  </div>
                  <div
                    className={`vd-stat${proposedCount > 0 ? " vd-stat--amber vd-stat--click" : ""}`}
                    onClick={proposedCount > 0 ? openReview : undefined}
                    role={proposedCount > 0 ? "button" : undefined}
                  >
                    <div className="vd-stat-label">Suggestions</div>
                    <div className="vd-stat-value">{proposedCount}</div>
                  </div>
                </div>

                <Card>
                  <div className="kt-record-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      Summary
                      {visit.summary && !audited && <Pill>AI draft</Pill>}
                    </span>
                    {!editingSummary && (
                      <Button size="sm" icon="pencil" onClick={() => { setSummaryDraft(visit.summary ?? ""); setEditingSummary(true); }}>
                        {visit.summary ? "Edit" : "Add summary"}
                      </Button>
                    )}
                  </div>
                  {editingSummary ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <TextArea autoFocus rows={6} value={summaryDraft} onChange={(e) => setSummaryDraft(e.target.value)}
                        placeholder="A short written summary of this visit…" />
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <Button onClick={() => setEditingSummary(false)}>Cancel</Button>
                        <Button variant="primary" icon="check" onClick={() => void saveSummary()}>Save summary</Button>
                      </div>
                    </div>
                  ) : visit.summary ? (
                    <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "var(--kt-ink)", whiteSpace: "pre-wrap", margin: 0 }}>{visit.summary}</p>
                  ) : (
                    <p style={{ fontSize: 13, color: "var(--kt-text-muted)", margin: 0 }}>
                      No summary yet — extraction drafts one when the transcript is ready, or write your own.
                    </p>
                  )}
                </Card>

                <Card>
                  <div className="kt-record-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      Notes for Next Visit
                      {visit.notesNextVisit && !audited && <Pill>AI draft</Pill>}
                    </span>
                    {!editingNotes && (
                      <Button size="sm" icon="pencil" onClick={() => { setNotesDraft(visit.notesNextVisit ?? ""); setEditingNotes(true); }}>
                        {visit.notesNextVisit ? "Edit" : "Add notes"}
                      </Button>
                    )}
                  </div>
                  {editingNotes ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <TextArea autoFocus rows={5} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder="Commitments and follow-ups for the next visit…" />
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <Button onClick={() => setEditingNotes(false)}>Cancel</Button>
                        <Button variant="primary" icon="check" onClick={() => void saveNotes()}>Save notes</Button>
                      </div>
                    </div>
                  ) : visit.notesNextVisit ? (
                    <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "var(--kt-ink)", whiteSpace: "pre-wrap", margin: 0 }}>{visit.notesNextVisit}</p>
                  ) : (
                    <p style={{ fontSize: 13, color: "var(--kt-text-muted)", margin: 0 }}>
                      No next-visit notes yet.
                    </p>
                  )}
                </Card>

                <Card>
                  <div className="kt-record-label" style={{ marginBottom: 10 }}>Sources</div>
                  <CustomerMeetingsTab customerId={customerId} visitId={visitId} meetings={meetings} onChanged={() => void load()} />
                </Card>
              </div>

              <div className="vd-col">
                <RecordCard
                  label="Details"
                  fields={[
                    { label: "Status", value: stageLabel(visit.status), strong: true },
                    { label: "Type", value: visit.type === "remote" ? "Remote" : "On-site" },
                    { label: "Date", value: dateRange, mono: true },
                    ...(visit.createdBy ? [{ label: "Created by", value: visit.createdBy, mono: true }] : []),
                    ...(visit.imported ? [{ label: "Source", value: "Imported from Confluence" }] : []),
                  ]}
                />
                <TicketsPanel customerId={customerId} />
                <Card>
                  <div className="kt-record-label" style={{ marginBottom: 10 }}>Reports</div>
                  <ReportSnapshots customerId={customerId} visitId={visitId} version={reportsVersion} />
                </Card>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "discussion" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {carryOver.length > 0 && (
            <Card>
              <div className="kt-record-label" style={{ marginBottom: 10 }}>Carried over from last visit</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {carryOver.map((item) => (
                  <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--kt-row-divider)" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: "var(--kt-ink)" }}>{item.topic}</div>
                      {item.notes && <div style={{ fontSize: 12, color: "var(--kt-text-muted)" }}>{item.notes}</div>}
                    </div>
                    <Button size="sm" icon="arrow-right" onClick={() => void acceptCarryOver(item)}>Add to this visit</Button>
                  </div>
                ))}
              </div>
            </Card>
          )}
          <Card>
            <div className="kt-record-label" style={{ marginBottom: 10 }}>
              Discussion items{acceptedItems.length ? ` · ${openItems} open of ${acceptedItems.length}` : ""}
            </div>
            <ItemsTable
              customerId={customerId}
              visitId={visitId}
              items={acceptedItems}
              onChanged={() => void load()}
              visitAudited={audited}
            />
          </Card>
        </div>
      )}

      {activeTab === "actions" && (
        <ActionsTab
          customerId={customerId}
          visitId={visitId}
          actions={actions}
          onChanged={() => void load()}
        />
      )}

      {activeTab === "files" && (
        <FilesTab customerId={customerId} visitId={visitId} reportsVersion={reportsVersion} />
      )}

      <Modal
        title="Mark visit audited?"
        open={auditOpen}
        onClose={() => !auditing && setAuditOpen(false)}
        actions={
          <>
            <Button onClick={() => setAuditOpen(false)} disabled={auditing}>Cancel</Button>
            <Button variant="primary" icon={auditing ? undefined : "lock-check"} disabled={auditing} onClick={() => void doAudit()}>
              {auditing && <span className="kt-spinner" style={{ width: 13, height: 13, borderWidth: 2, marginRight: 6 }} />}
              {auditing ? "Marking audited…" : "Mark audited"}
            </Button>
          </>
        }
      >
        <p style={{ fontSize: 13, color: "var(--kt-text-secondary)", margin: 0 }}>
          This deletes the meeting audio and publishes the visit summary to the brain. This can&apos;t be undone.
        </p>
      </Modal>

      <Modal
        title="Delete this site visit?"
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        actions={
          <>
            <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
            <Button variant="danger" icon={deleting ? undefined : "trash"} disabled={deleting} onClick={() => void doDelete()}>
              {deleting && <span className="kt-spinner" style={{ width: 13, height: 13, borderWidth: 2, marginRight: 6 }} />}
              {deleting ? "Deleting…" : "Delete visit"}
            </Button>
          </>
        }
      >
        <p style={{ fontSize: 13, color: "var(--kt-text-secondary)", margin: 0 }}>
          This permanently removes <strong>{visit.title}</strong> and everything under it — meetings, transcripts,
          discussion items, actions, exported reports and files. This can&apos;t be undone.
        </p>
      </Modal>

      <ImportTranscriptModal
        customerId={customerId}
        visitId={visitId}
        open={importOpen}
        initialFile={droppedFile}
        onClose={() => setImportOpen(false)}
        onDone={() => void load()}
      />
    </div>
  );
}

function ReportSnapshots({ customerId, visitId, version }: { customerId: string; visitId: string; version: number }) {
  const [files, setFiles] = useState<StoreObject[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listFiles(`customers/${customerId}/visits/${visitId}/reports/`)
      .then((objs) => { if (!cancelled) setFiles(objs); })
      .catch(() => { if (!cancelled) setFiles([]); });
    return () => { cancelled = true; };
  }, [customerId, visitId, version]);

  const open = async (key: string) => {
    try {
      window.open(await presignFile(key), "_blank", "noopener");
    } catch { /* best-effort */ }
  };

  if (files === null) return <p style={{ fontSize: 13, color: "var(--kt-text-muted)", margin: 0 }}>Loading…</p>;
  if (files.length === 0) {
    return (
      <p style={{ fontSize: 13, color: "var(--kt-text-muted)", margin: 0 }}>
        No reports exported yet — use <strong>Export report</strong> to freeze HTML + PDF.
      </p>
    );
  }
  const sorted = [...files].sort((a, b) => (b.lastModified ?? "").localeCompare(a.lastModified ?? ""));

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12, color: "var(--kt-text-muted)" }}>
      {sorted.map((f) => {
        const name = f.key.split("/").pop() ?? f.key;
        const tag = name.endsWith(".pdf") ? "PDF" : name.endsWith(".html") ? "HTML" : null;
        return (
          <button
            key={f.key}
            className="kt-menu-item"
            style={{ width: "auto", padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 6 }}
            title={f.key}
            onClick={() => void open(f.key)}
          >
            {f.lastModified ? new Date(f.lastModified).toLocaleString() : name}
            {tag && <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.75 }}>{tag}</span>}
          </button>
        );
      })}
    </div>
  );
}

function TicketsPanel({ customerId }: { customerId: string }) {
  const [tickets, setTickets] = useState<CustomerTickets | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setTickets(null);
    setFailed(false);
    getCustomerTickets(customerId)
      .then((r) => { if (!cancelled) setTickets(r); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [customerId]);

  return (
    <Card>
      <div className="kt-record-label" style={{ marginBottom: 10 }}>Open tickets</div>
      {failed ? (
        <p style={{ fontSize: 13, color: "var(--kt-text-muted)", margin: 0 }}>Couldn&apos;t load tickets — try refreshing.</p>
      ) : !tickets ? (
        <CenteredSpinner label="Loading tickets…" />
      ) : tickets.orgId === null ? (
        <NoTicketMappingNote />
      ) : (() => {
        const openTickets = tickets.tickets.filter((t) => t.statusCategory !== "done");
        return openTickets.length === 0
          ? <EmptyState icon="ticket" title="No open tickets" />
          : <TicketsTable tickets={openTickets} />;
      })()}
    </Card>
  );
}
